/**
 * Whether the dev overlay is on.
 *
 * Read once from `?dev=1` and then held in `sessionStorage`, the same mechanism
 * and the same reasoning as `ShellContext`: the query string does not survive the
 * first in-app navigation, and an overlay that vanished on the second screen would
 * be useless for walking a colleague through the flow.
 *
 * Two things this is deliberately not:
 *
 *   - **not a feature flag.** `pre2postB2bEnabled` in the mock BFF is a product
 *     flag with an open question attached (Q-27). This is a POC affordance and has
 *     no counterpart in anything Orange will run.
 *   - **not a security boundary.** `devGuard` keeps `/dev/**` out of the way of
 *     someone clicking around, nothing more. Everything behind it is in the same
 *     bundle and the flag is a query parameter; treating it as protection would be
 *     a mistake in a POC that has no authentication to begin with.
 *
 * The overlay itself is Phase 9. This exists now because `devGuard` needs
 * something to ask, and the answer should not be spelled two different ways.
 */
import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'pre2post.poc.dev.v1';

@Injectable({ providedIn: 'root' })
export class DevMode {
  private readonly enabledSignal = signal<boolean>(initialEnabled());

  readonly enabled = this.enabledSignal.asReadonly();

  /** `?dev=1` in the URL, the keyboard chord, and the overlay's own off switch. */
  set(enabled: boolean): void {
    this.enabledSignal.set(enabled);
    try {
      sessionStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
    } catch {
      // Private browsing, or storage disabled. The toggle still applies to this
      // page load; it simply will not survive a navigation.
    }
  }

  toggle(): void {
    this.set(!this.enabledSignal());
  }
}

function initialEnabled(): boolean {
  try {
    // `?dev=0` has to work as well as `?dev=1`, so a link can turn the overlay off
    // for someone whose session has it on.
    const fromUrl = new URLSearchParams(location.search).get('dev');
    if (fromUrl === '1' || fromUrl === '0') {
      const enabled = fromUrl === '1';
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
      return enabled;
    }
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
